import { useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import { uid, sanitize, isOralDelivery, localDateStr } from '../lib/utils';
import { supportsBlend, normalizeBlend, blendConc, blendVialConc } from '../lib/blend';

// Every recall action patches one vial in ONE upd() — two upd()s built from the same `stored`
// closure would drop the first change.
const patchVial = (stored, cmpd, vid, fn) => ({
  vials: { ...stored.vials, [cmpd]: (stored.vials[cmpd] || []).map(v => (v.id === vid ? fn(v) : v)) },
});

export function useVialActions({ stored, upd, getCmpd, showToast, setPendingConfirm, newVialD, setAVF, setNVD, editVialId, editVialD, setEVI, setEVD }) {
  const concToastTimer = useRef(null);

  const saveNewVial = useCallback(() => {
    const qty = parseInt(newVialD.quantity) || 1;
    const orderTotal = parseFloat(newVialD.orderCost) || 0;
    const costPerVial = orderTotal > 0 ? (orderTotal / qty) : 0;
    const batchId = qty > 1 ? uid() : null;
    const cmpdDeliveryType = getCmpd(newVialD.compound)?.deliveryType || 'syringe';
    const totalDoses = parseInt(newVialD.totalDoses) || (cmpdDeliveryType === 'autoinjector' ? 1 : 0);
    const resolvedStatus = newVialD.status || "new";
    const resolvedOpenedAt = resolvedStatus === "open" && !newVialD.openedAt
      ? new Date().toISOString().slice(0, 10)
      : (newVialD.openedAt || "");
    // A vial only ever transitions from status "new" to "open" (the point at which
    // calcRemaining starts actually subtracting logged doses) as a side effect of its
    // first logged dose — and only if `active` was already true at that moment. Force
    // activation for a compound's first vial regardless of the "Set as active" checkbox,
    // so supply tracking works from day one instead of silently never starting. A second/
    // backup vial for a compound that already has one still respects the checkbox as before.
    const hasExistingVial = (stored.vials[newVialD.compound] || []).some(v => v.status !== 'archived');
    const resolvedActive = !hasExistingVial || !!newVialD.active;
    // pillsPerDose only means anything for oral/pill delivery (see Vial data model in CLAUDE.md).
    // VialForm's Stack Item dropdown doesn't clear data.pillsPerDose when switched to a pen/
    // autoinjector compound, so without this gate a stray value typed while a pill compound was
    // selected could persist onto a pen/autoinjector vial and silently multiply its cost/dose.
    const pillsPerDose = isOralDelivery(cmpdDeliveryType) ? (parseInt(newVialD.pillsPerDose) || undefined) : undefined;
    // Blend vial: the recipe sum is the vial's total mg, so conc is derived, never typed.
    const blend = supportsBlend(cmpdDeliveryType) ? normalizeBlend(newVialD.blend) : undefined;
    const conc = blend ? blendConc(blend, newVialD.totalVol) : (parseFloat(newVialD.conc) || 0);
    const base = { compound: newVialD.compound, label: sanitize(newVialD.label || "New Vial", 200), pharmacy: sanitize(newVialD.pharmacy || "", 200), conc, ...(blend ? { blend } : {}), totalVol: parseFloat(newVialD.totalVol) || 0, totalDoses: totalDoses || undefined, dosePerInjection: parseFloat(newVialD.dosePerInjection) || undefined, pillsPerDose, status: resolvedStatus, openedAt: resolvedOpenedAt, bud: newVialD.bud || "", budAck: !!newVialD.budAck, receivedDate: newVialD.receivedDate || "", lot: sanitize(newVialD.lot || "", 100), notes: sanitize(newVialD.notes || "", 500), active: !!newVialD.active, costPerVial: costPerVial || null, orderTotal: orderTotal || null, orderQty: qty > 1 ? qty : null, batchId };
    const cmpd = newVialD.compound;
    const newVials = Array.from({ length: qty }, (_, i) => ({ ...base, id: uid(), active: resolvedActive && i === 0 }));
    // Only one vial per compound should ever be active (matches setActiveV's invariant
    // below) -- without this, checking "Set as active" on a new vial while another was
    // already active left both flagged active, and logging kept silently drawing from
    // whichever came first in the array instead of the one the user just chose.
    const existingVials = resolvedActive
      ? (stored.vials[cmpd] || []).map(v => ({ ...v, active: false }))
      : (stored.vials[cmpd] || []);
    // "Also make this the default" copies this vial's recipe + BAC onto the stack item.
    const compoundsPatch = blend && newVialD.makeBlendDefault
      ? { compounds: { ...stored.compounds, [cmpd]: { ...stored.compounds[cmpd], blend, bacMl: parseFloat(newVialD.totalVol) || undefined } } }
      : {};
    upd({ vials: { ...stored.vials, [cmpd]: [...existingVials, ...newVials] }, ...compoundsPatch });
    posthog.capture('vial_added', { compound: cmpd, quantity: qty });
    const containerWord = cmpdDeliveryType === 'pen' ? 'pen' : cmpdDeliveryType === 'autoinjector' ? 'auto-injector' : 'vial';
    setAVF(null); setNVD({}); showToast(`${qty} ${containerWord}${qty > 1 ? "s" : ""} added ✓`);
  }, [stored, newVialD, getCmpd, upd, setAVF, setNVD, showToast]);

  const saveVialEdit = useCallback(() => {
    // totalMg and makeBlendDefault are VialForm-only convenience fields (see VialForm.jsx) —
    // never part of the persisted vial shape, so they're stripped before spreading `rest`
    // below, the same way saveNewVial's explicit field whitelist never references them.
    // blend is pulled out too so it's normalized (or cleared) explicitly rather than spread raw.
    const { cmpd, totalMg, makeBlendDefault, blend: blendRows, ...rest } = editVialD;
    const existing = stored.vials[cmpd].find(v => v.id === editVialId);
    const cmpdDT = getCmpd(cmpd)?.deliveryType || 'syringe';
    const blend = supportsBlend(cmpdDT) ? normalizeBlend(blendRows) : undefined;
    const totalDoses = parseInt(rest.totalDoses) || existing?.totalDoses || (cmpdDT === 'autoinjector' ? 1 : undefined);
    const resolvedStatus = rest.status || existing?.status || 'new';
    const resolvedOpenedAt = resolvedStatus === 'open' && !rest.openedAt && !existing?.openedAt
      ? new Date().toISOString().slice(0, 10)
      : (rest.openedAt ?? existing?.openedAt ?? '');
    const updated = {
      ...existing, ...rest,
      status: resolvedStatus, openedAt: resolvedOpenedAt,
      label: sanitize(rest.label || '', 200),
      pharmacy: sanitize(rest.pharmacy || '', 200),
      lot: sanitize(rest.lot || '', 100),
      notes: sanitize(rest.notes || '', 500),
      conc: blend ? blendVialConc(blend, rest.totalVol, existing) : (parseFloat(rest.conc) || 0),
      totalVol: parseFloat(rest.totalVol) || 0,
      ...(totalDoses ? { totalDoses } : {}),
    };
    // blend was destructured out of rest, so set or clear it explicitly (existing may carry an old one).
    if (blend) updated.blend = blend; else delete updated.blend;
    const compoundsPatch = blend && makeBlendDefault
      ? { compounds: { ...stored.compounds, [cmpd]: { ...stored.compounds[cmpd], blend, bacMl: parseFloat(rest.totalVol) || undefined } } }
      : {};
    upd({ vials: { ...stored.vials, [cmpd]: stored.vials[cmpd].map(v => v.id === editVialId ? updated : v) }, ...compoundsPatch });
    setEVI(null); setEVD({}); showToast("Updated ✓");
  }, [stored, editVialId, editVialD, getCmpd, upd, setEVI, setEVD, showToast]);

  const duplicateVial = useCallback((cmpd, vial) => {
    // budAckExpired/budDismissed are per-vial-instance review state, not properties of the
    // batch (unlike `bud` itself, which carries over since a duplicate is typically another
    // vial from the same lot/order and shares its printed expiry) — a duplicate is a distinct
    // physical vial that hasn't been reviewed yet, so it must show its own BUD warning fresh.
    // The same goes for recall state: the copy shows its own FDA match, unacknowledged.
    const copy = { ...vial };
    delete copy.recall;
    delete copy.recallAcks;
    upd({ vials: { ...stored.vials, [cmpd]: [...(stored.vials[cmpd] || []), { ...copy, id: uid(), active: false, status: "new", openedAt: "", budAck: false, budAckExpired: false, budDismissed: false, budApproachDismissed: false }] } });
    showToast("Duplicated ✓");
  }, [stored, upd, showToast]);

  const archiveVial = useCallback((cmpd, vid) => {
    upd({ vials: { ...stored.vials, [cmpd]: stored.vials[cmpd].map(v => v.id === vid ? { ...v, status: "archived", active: false } : v) } });
    showToast("Archived");
  }, [stored, upd, showToast]);

  const unarchiveVial = useCallback((cmpd, vid) => {
    upd({ vials: { ...stored.vials, [cmpd]: stored.vials[cmpd].map(v => v.id === vid ? { ...v, status: "new" } : v) } });
    showToast("Unarchived");
  }, [stored, upd, showToast]);

  const deleteVial = useCallback((cmpd, vid) => {
    const dt = getCmpd(cmpd)?.deliveryType;
    const word = dt === 'pen' ? 'pen' : dt === 'autoinjector' ? 'auto-injector' : 'vial';
    setPendingConfirm({
      message: `Delete this ${word}?`,
      onConfirm: () => { upd({ vials: { ...stored.vials, [cmpd]: (stored.vials[cmpd] || []).filter(v => v.id !== vid) } }); showToast("Removed"); }
    });
  }, [stored, upd, getCmpd, setPendingConfirm, showToast]);

  const setActiveV = useCallback((cmpd, vid) => {
    const prevActive = (stored.vials[cmpd] || []).find(v => v.active && v.status !== "empty");
    const newVial = (stored.vials[cmpd] || []).find(v => v.id === vid);
    if (prevActive && newVial && prevActive.conc && newVial.conc && prevActive.conc !== newVial.conc) {
      showToast(`⚠ Concentration changed: ${prevActive.conc}mg/mL → ${newVial.conc}mg/mL. Check your dose volume!`);
      if (concToastTimer.current) clearTimeout(concToastTimer.current);
      concToastTimer.current = setTimeout(() => showToast(`Previous: ${prevActive.conc}mg/mL · New: ${newVial.conc}mg/mL — recalculate your draw`), 3200);
    }
    upd({ vials: { ...stored.vials, [cmpd]: stored.vials[cmpd].map(v => ({ ...v, active: v.id === vid })) } });
  }, [stored, upd, showToast]);

  const ackBudExpired = useCallback(vid => upd({
    vials: Object.fromEntries(Object.entries(stored.vials).map(([k, vs]) => [k, vs.map(v => v.id === vid ? { ...v, budAckExpired: true } : v)]))
  }), [stored, upd]);

  const dismissBudWarning = useCallback(vid => upd({
    vials: Object.fromEntries(Object.entries(stored.vials).map(([k, vs]) => [k, vs.map(v => v.id === vid ? { ...v, budDismissed: true } : v)]))
  }), [stored, upd]);

  // Distinct from budDismissed/budAckExpired: this is the proactive "BUD is coming up in the
  // next 30 days" nudge (Tracker.jsx's `newAlerts` effect), not the "already passed" warning.
  const dismissBudApproaching = useCallback(vid => upd({
    vials: Object.fromEntries(Object.entries(stored.vials).map(([k, vs]) => [k, vs.map(v => v.id === vid ? { ...v, budApproachDismissed: true } : v)]))
  }), [stored, upd]);

  const ackRecall = useCallback((cmpd, vid, recallId) => upd(patchVial(stored, cmpd, vid, v => ({
    ...v, recallAcks: { ...(v.recallAcks || {}), [recallId]: localDateStr() },
  }))), [stored, upd]);

  // "Stop using" from an FDA alert: archive and acknowledge together.
  const stopUsingRecalledVial = useCallback((cmpd, vid, recallId) => {
    upd(patchVial(stored, cmpd, vid, v => ({
      ...v, status: 'archived', active: false,
      recallAcks: { ...(v.recallAcks || {}), [recallId]: localDateStr() },
    })));
    showToast('Archived — stopped using');
  }, [stored, upd, showToast]);

  const markVialRecalled = useCallback((cmpd, vid, { date, reason, link, notes, stopUsing } = {}) => {
    const today = localDateStr();
    const r = sanitize(reason || '', 300), n = sanitize(notes || '', 500), l = sanitize(link || '', 500);
    const recall = {
      source: 'manual', date: date || today, ackAt: today,
      ...(r ? { reason: r } : {}), ...(n ? { notes: n } : {}),
      ...(/^https?:\/\//i.test(l) ? { link: l } : {}),
    };
    upd(patchVial(stored, cmpd, vid, v => ({ ...v, recall, ...(stopUsing ? { status: 'archived', active: false } : {}) })));
    showToast('Marked as recalled');
  }, [stored, upd, showToast]);

  const unmarkVialRecalled = useCallback((cmpd, vid) => {
    setPendingConfirm({
      message: 'Remove your recall mark from this vial?',
      confirmLabel: 'Remove mark',
      onConfirm: () => {
        upd(patchVial(stored, cmpd, vid, v => { const next = { ...v }; delete next.recall; return next; }));
        showToast('Recall mark removed');
      },
    });
  }, [stored, upd, setPendingConfirm, showToast]);

  return { saveNewVial, saveVialEdit, duplicateVial, archiveVial, unarchiveVial, deleteVial, setActiveV, ackBudExpired, dismissBudWarning, dismissBudApproaching, ackRecall, stopUsingRecalledVial, markVialRecalled, unmarkVialRecalled };
}
